/**
 * Entrega de um resultado calculado para a página de interpretação (/avaliacao/:token).
 *
 * Fica fora de ResultModal.jsx/MiniMentalCalculator.jsx/guidedFlow.js de propósito:
 * tests/redesign-guards.test.js proíbe sessionStorage, localStorage e navigate() nesses
 * arquivos (nada do fluxo de cálculo deve ir para armazenamento ou navegação imperativa).
 * Este módulo é a única porta para o handoff — os arquivos vigiados só chamam estas
 * funções e navegam com um <Link> declarativo.
 *
 * O token é um UUID aleatório (122 bits), não derivado da pontuação nem de dado do
 * paciente. Os dados ficam em sessionStorage (nunca localStorage): desaparecem quando a
 * aba fecha, nunca são enviados a um servidor e expiram em 30 minutos mesmo que a aba
 * continue aberta.
 */
const PREFIX = 'minimental:avaliacao:';
const TTL_MS = 30 * 60 * 1000;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function storage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function prune(store) {
  const now = Date.now();
  Object.keys(store).filter((key) => key.startsWith(PREFIX)).forEach((key) => {
    try {
      const { savedAt } = JSON.parse(store.getItem(key));
      if (!savedAt || now - savedAt > TTL_MS) store.removeItem(key);
    } catch {
      store.removeItem(key);
    }
  });
}

/** Grava o snapshot local e devolve o token da URL. Falha silenciosa: sem armazenamento, a página de destino mostra "sessão expirada". */
export function createAnonymousHandoff(snapshot) {
  const token = crypto.randomUUID();
  const store = storage();
  if (!store) return token;
  try {
    prune(store);
    store.setItem(PREFIX + token, JSON.stringify({ ...snapshot, savedAt: Date.now() }));
  } catch {
    // sessionStorage indisponível ou cheio: a leitura em /avaliacao/:token vai falhar de forma segura.
  }
  return token;
}

export function readAnonymousHandoff(token) {
  if (!UUID_RE.test(String(token || ''))) return null;
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(PREFIX + token);
    if (!raw) return null;
    const { savedAt, ...snapshot } = JSON.parse(raw);
    if (!savedAt || Date.now() - savedAt > TTL_MS) {
      store.removeItem(PREFIX + token);
      return null;
    }
    return snapshot;
  } catch {
    return null;
  }
}

export function savedHandoffToken(assessmentId) {
  return `s-${assessmentId}`;
}

export function isSavedToken(token) {
  return /^s-\d+$/.test(String(token || ''));
}

export function savedAssessmentIdFromToken(token) {
  const match = /^s-(\d+)$/.exec(String(token || ''));
  return match ? match[1] : null;
}
